import type { AdminSelf } from '@saay/api-client';
import { IconTile, StatusBadge } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { Building2Icon } from 'lucide-react';
import { formatCount, formatTomanCompact, statusLabel } from '@saay/formatting';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LinearProgress,
  LoadMore,
  PageHeader,
  Section,
  Skeleton,
  StatusChip,
  Table,
  Tabs,
  useSearchInput,
} from '../kit.js';
import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';

import type { OrganizationSummary } from '../api.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { ListSearchInput } from '../components/list-filters.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { OUTLINE_PILL } from '../components/surface-section.js';
import { canUse } from '../permissions.js';
import {
  listStatus,
  type OrganizationFilters,
  useOrganizationRoles,
  useOrganizations,
} from '../queries.js';
import {
  ORGANIZATION_TYPES,
  OrganizationRoleDialog,
  organizationTypeLabel,
} from './organization-forms.js';

/**
 * `/organizations` (FTD §9.5, prototype «سازمان‌ها، اعضا و اعتبار»): the S7 tiles, the organization list
 * (O1) and the global role catalog (O8) as tabs, «نقش سراسری» and «سازمان جدید». The organization
 * drawer, the create sheet, and the enrollment sheets are child routes over this list.
 */

export interface OrganizationsOutletContext {
  readonly self: AdminSelf;
}

type Tab = 'list' | 'roles';

/** `/organizations/:organizationId` is a full page (W4: it rendered under the list). */
function isDetailPage(pathname: string): boolean {
  return /^\/organizations\/[^/]+$/.test(pathname) && pathname !== '/organizations/new';
}

export function OrganizationsPage() {
  const { pathname } = useLocation();
  usePageTitle('سازمان‌ها', pathname.replace(/\/$/, '') === '/organizations');
  return (
    <RequireArea area="organizations">
      {(self) =>
        isDetailPage(pathname) ? (
          <Outlet context={{ self } satisfies OrganizationsOutletContext} />
        ) : (
          <OrganizationsView self={self} />
        )
      }
    </RequireArea>
  );
}

function readFilters(params: URLSearchParams): OrganizationFilters {
  const filters: Record<string, string> = {};
  for (const key of ['q', 'status', 'organizationType'] as const) {
    const value = params.get(key);
    if (value !== null && value !== '') filters[key] = value;
  }
  return filters as OrganizationFilters;
}

function OrganizationsView({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const [roleOpen, setRoleOpen] = useState(false);
  const tab: Tab = params.get('tab') === 'roles' ? 'roles' : 'list';
  const manage = canUse(self, 'createOrganization');

  function change(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  return (
    <>
      <PageHeader
        actions={
          manage ? (
            <div className="admin-actions">
              <Button
                className={OUTLINE_PILL}
                onClick={() => setRoleOpen(true)}
                variant="secondary"
              >
                <Icon name="plus" />
                نقش سراسری
              </Button>
              <Link
                className={buttonVariants({ className: 'rounded-full px-5' })}
                state={OPENED_FROM_LIST}
                to="/organizations/new"
              >
                <Icon name="plus" />
                سازمان جدید
              </Link>
            </div>
          ) : undefined
        }
        title="سازمان‌ها"
      />
      <Tabs
        label="بخش‌های مدیریت سازمان"
        onChange={(next) => change({ tab: next === 'list' ? '' : next })}
        panel={
          tab === 'roles' ? (
            <RolesPanel manage={manage} onCreate={() => setRoleOpen(true)} />
          ) : (
            <OrganizationListPanel filters={readFilters(params)} onChange={change} self={self} />
          )
        }
        tabs={[
          { key: 'list', label: 'سازمان‌ها' },
          { key: 'roles', label: 'نقش‌های سراسری' },
        ]}
        value={tab}
      />
      {roleOpen ? (
        <OrganizationRoleDialog
          onOpenChange={(next) => {
            if (!next) setRoleOpen(false);
          }}
        />
      ) : null}
      <Outlet context={{ self } satisfies OrganizationsOutletContext} />
    </>
  );
}

function organizationExposureLabel(row: OrganizationSummary): string {
  const exposure = row.dedicatedProgramExposure;
  if (exposure === undefined) return '—';
  if (exposure.activeProgramCount === 0) return 'بدون برنامهٔ اختصاصی';
  return `${formatTomanCompact(exposure.committedIrr)} / ${formatTomanCompact(exposure.totalExposureCapIrr)}`;
}

function OrganizationExposureMeter({ row }: { readonly row: OrganizationSummary }) {
  const exposure = row.dedicatedProgramExposure;
  if (exposure === undefined) return <>—</>;
  if (exposure.activeProgramCount === 0) {
    return <span className="admin-muted">بدون برنامهٔ اختصاصی</span>;
  }
  const rate =
    exposure.totalExposureCapIrr === 0
      ? 0
      : (exposure.committedIrr / exposure.totalExposureCapIrr) * 100;
  const label = organizationExposureLabel(row);
  return (
    <div className="admin-program-exposure">
      <LinearProgress aria-label={label} label={label} value={rate} />
      <span className="admin-program-exposure__label">{label}</span>
    </div>
  );
}

function OrganizationListPanel({
  filters,
  onChange,
  self,
}: {
  readonly filters: OrganizationFilters;
  readonly onChange: (changes: Record<string, string>) => void;
  readonly self: AdminSelf;
}) {
  const { search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const organizations = useOrganizations(filters);
  const rows = organizations.data?.pages.flatMap((page) => page.items) ?? [];
  const search = useSearchInput({
    applied: filters.q ?? '',
    invalidMessage: 'برای جست‌وجو دست‌کم ۳ حرف وارد کنید.',
    minLength: 3,
    onApply: (q) => onChange({ q }),
  });
  const filtered = Object.keys(filters).length > 0;
  const open = (id: string) => ({ pathname: `/organizations/${id}`, search: locationSearch });
  return (
    <Section id="organization-list" title="سازمان‌های ثبت‌شده">
      <FilterPillBar
        activeFilterCount={[filters.organizationType, filters.status].filter(Boolean).length}
        label="فیلتر سازمان‌ها"
        onClearAll={() => onChange({ organizationType: '', status: '', q: '' })}
      >
        <form
          className="shrink-0"
          onSubmit={(event) => {
            event.preventDefault();
            search.submit();
          }}
        >
          <ListSearchInput
            invalid={search.message !== undefined}
            label="جست‌وجوی سازمان"
            onChange={(event) => search.setText(event.target.value)}
            placeholder="نام سازمان"
            value={search.text}
          />
        </form>
        <FilterPill
          emptyLabel="همه نوع‌ها"
          label="نوع سازمان"
          onChange={(organizationType) => onChange({ organizationType })}
          options={Object.entries(ORGANIZATION_TYPES).map(([value, label]) => ({ label, value }))}
          value={filters.organizationType ?? ''}
        />
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت"
          onChange={(status) => onChange({ status })}
          options={(['ACTIVE', 'INACTIVE'] as const).map((value) => ({
            label: statusLabel('activeStatus', value),
            value,
          }))}
          value={filters.status ?? ''}
        />
      </FilterPillBar>
      {search.message === undefined ? null : (
        <p className="mb-3 text-sm text-destructive">{search.message}</p>
      )}
      <QueryState
        empty={
          <EmptyState
            action={
              !filtered && canUse(self, 'createOrganization') ? (
                <Link className={buttonVariants()} state={OPENED_FROM_LIST} to="/organizations/new">
                  تعریف اولین سازمان
                </Link>
              ) : undefined
            }
            description={
              filtered
                ? 'فیلترها را تغییر دهید یا پاک کنید.'
                : 'پس از تعریف، سازمان‌ها اینجا دیده می‌شوند.'
            }
            icon="users"
            title={filtered ? 'سازمانی با این فیلترها پیدا نشد' : 'هنوز سازمانی تعریف نشده است'}
          />
        }
        error={organizations.error}
        isEmpty={rows.length === 0}
        onRetry={() => void organizations.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری سازمان‌ها…" lines={6} />}
        status={listStatus(organizations)}
      >
        <AdminListTable
          caption="فهرست سازمان‌ها"
          columns={[
            {
              header: 'نام',
              id: 'name',
              pin: 'start',
              render: (row) => (
                <span className="flex items-center gap-3">
                  <IconTile
                    icon={Building2Icon}
                    size="sm"
                    tone={row.status === 'ACTIVE' ? 'olive' : 'forest'}
                  />
                  <span className="saay-cell-main">
                    <Link className="font-bold" state={OPENED_FROM_LIST} to={open(row.id)}>
                      {row.displayNameFa}
                    </Link>
                    {row.guarantorExempt ? null : <StatusBadge tone="clay">ضامن لازم</StatusBadge>}
                  </span>
                </span>
              ),
            },
            {
              header: 'نوع / شهر',
              id: 'type',
              render: (row) => (
                <>
                  {organizationTypeLabel(row.organizationType)}
                  {` · ${row.city.nameFa}`}
                </>
              ),
            },
            {
              header: 'عضو فعال',
              id: 'members',
              numeric: true,
              render: (row) => formatCount(row.activeMemberCount),
            },
            {
              header: 'تعهد اختصاصی',
              id: 'exposure',
              render: (row) => <OrganizationExposureMeter row={row} />,
            },
            {
              header: 'وضعیت',
              id: 'status',
              render: (row) => <StatusChip enumName="activeStatus" value={row.status} />,
            },
          ]}
          mobileSummary={(row) => ({
            primary: row.displayNameFa,
            secondary: <StatusChip enumName="activeStatus" value={row.status} />,
          })}
          onRowClick={(row) => navigate(open(row.id), { state: OPENED_FROM_LIST })}
          rowKey={(row) => row.id}
          rows={rows}
        />
        {organizations.isFetchNextPageError ? (
          <ErrorState
            error={organizations.error}
            onRetry={() => void organizations.fetchNextPage()}
          />
        ) : null}
        <LoadMore
          hasMore={organizations.hasNextPage}
          loading={organizations.isFetchingNextPage}
          onLoadMore={() => void organizations.fetchNextPage()}
        />
      </QueryState>
    </Section>
  );
}

function RolesPanel({
  manage,
  onCreate,
}: {
  readonly manage: boolean;
  readonly onCreate: () => void;
}) {
  const roles = useOrganizationRoles({ sort: 'roleCode' });
  const rows = roles.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Section
      actions={
        manage ? (
          <Button onClick={onCreate} size="sm">
            <Icon name="plus" />
            نقش جدید
          </Button>
        ) : undefined
      }
      id="organization-roles"
      title="نقش‌های سراسری"
    >
      <QueryState
        empty={<EmptyState icon="users" title="هنوز نقشی تعریف نشده است" />}
        error={roles.error}
        isEmpty={rows.length === 0}
        onRetry={() => void roles.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری نقش‌ها…" lines={4} />}
        status={listStatus(roles)}
      >
        <Table
          caption="کاتالوگ نقش‌های سازمانی"
          regionLabel="جدول نقش‌های سازمانی"
          columns={[
            { header: 'نقش سراسری', key: 'name', pin: 'start', render: (row) => row.nameFa },
            {
              header: 'کد ثابت',
              key: 'code',
              render: (row) => <bdi dir="ltr">{row.roleCode}</bdi>,
            },
            { header: 'دسته', key: 'category', render: (row) => row.category ?? '—' },
            {
              header: 'سازمان استفاده‌کننده',
              key: 'organizations',
              numeric: true,
              render: (row) => `${formatCount(row.organizationCount)} سازمان`,
            },
            {
              header: 'عضو فعال',
              key: 'members',
              numeric: true,
              render: (row) => formatCount(row.activeMembershipCount),
            },
            {
              header: 'وضعیت',
              key: 'status',
              pin: 'end',
              render: (row) => <StatusChip enumName="activeStatus" value={row.status} />,
            },
          ]}
          rowKey={(row) => row.id}
          rows={rows}
        />
        <LoadMore
          hasMore={roles.hasNextPage}
          loading={roles.isFetchingNextPage}
          onLoadMore={() => void roles.fetchNextPage()}
        />
      </QueryState>
    </Section>
  );
}
